import { useState, FormEvent, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuth } from '../../lib/auth.context'
import { useRedirectUrl } from '../../hooks/useRedirectUrl'
import { supabase } from '../../lib/supabase'
import { Card } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Button } from '../../components/ui/button'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { useToast } from '../../hooks/use-toast'

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isFormValid, setIsFormValid] = useState(false)
  
  // Update form validity whenever email or password changes
  useEffect(() => {
    setIsFormValid(email.trim() !== '' && password.trim() !== '')
  }, [email, password])
  const [formError, setFormError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [redirectTarget, setRedirectTarget] = useState<string | null>(null)
  const { login, isAuthenticated } = useAuth()
  const { getStoredUrl, clearStoredUrl } = useRedirectUrl()
  const navigate = useNavigate()
  const location = useLocation()
  const { toast } = useToast()

  // Check for redirect URL on component mount
  useEffect(() => {
    const storedUrl = getStoredUrl()
    if (storedUrl) {
      setRedirectTarget(storedUrl)
      console.log('[LoginPage] Found redirect target:', storedUrl)
    }

    // Check for redirect in query params (e.g., ?redirect=/settings)
    const params = new URLSearchParams(location.search)
    const redirectParam = params.get('redirect')
    if (redirectParam) {
      setRedirectTarget(redirectParam)
      console.log('[LoginPage] Found redirect param:', redirectParam)
    }
  }, [getStoredUrl, location.search])

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const target = redirectTarget || '/'
      console.log('[LoginPage] Already authenticated, redirecting to:', target)
      clearStoredUrl()
      navigate(target, { replace: true })
    }
  }, [isAuthenticated, navigate, redirectTarget, clearStoredUrl])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setFormError(null)
    setIsLoading(true)

    try {
      await login(email, password)
      
      // Check if MFA is enabled
      const { data: factors } = await supabase.auth.mfa.listFactors()
      
      if (factors?.totp && factors.totp.length > 0) {
        // User has TOTP set up, redirect to verification with the final redirect target
        const redirectTo = redirectTarget || '/'
        navigate(`/auth/verify-totp?redirect=${encodeURIComponent(redirectTo)}`, { replace: true })
      } else {
        // User needs to set up TOTP, redirect to registration with the final redirect target
        const redirectTo = redirectTarget || '/'
        navigate(`/auth/totp-registration?redirect=${encodeURIComponent(redirectTo)}`, { replace: true })
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to login'
      setFormError(errorMessage)
      toast({
        title: 'Login failed',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex h-screen w-full items-center justify-center bg-black" style={{ fontFamily: 'Text md/Semibold, sans-serif', fontSize: '14px' }}>
      <Card className="w-full max-w-md p-8 bg-[#121212] text-[#CECFD2] border-0 shadow-xl">
        <div className="flex justify-center mb-6">
          <img src="/logo.png" alt="Club Logo" className="h-12" />
        </div>
        <h1 className="mb-6 text-2xl font-semibold text-center text-[#CECFD2]">Club Login</h1>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              className="w-full bg-[#26222E] border-0 text-[#CECFD2] placeholder:text-gray-500 h-12"
            />
          </div>
          <div>
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              className="w-full bg-[#26222E] border-0 text-[#CECFD2] placeholder:text-gray-500 h-12"
            />
          </div>
          {formError && (
            <Alert variant="destructive">
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}
          <Button
            type="submit"
            className={`w-full font-semibold h-12 ${
              isFormValid && !isLoading
                ? 'bg-[#4E46FF] hover:bg-[#3d37cc] text-white'
                : 'bg-[#26222E] text-[#CECFD2] opacity-50 cursor-not-allowed'
            }`}
            disabled={isLoading || !isFormValid}
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </Button>
          <div className="mt-4 text-center">
            <Link to="/auth/forgot-password" className="text-sm text-[#CECFD2] hover:text-gray-300">
              Forgot your password?
            </Link>
          </div>
        </form>
      </Card>
    </div>
  )
}